import { Fragment } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { endpoints } from "../lib/api";
import { clock } from "./Cited";

/* A project's Overview, Timeline, minutes and transcripts searched for the
 * words typed (src/api/project_search.py): plain text, part of a word
 * matches, no AI. Each result opens its meeting, a time at that moment. */

export function ProjectSearch({ projectId, query }: { projectId: number; query: string }) {
  const { data, error, isFetching } = useQuery({
    queryKey: ["project-search", projectId, query],
    queryFn: () => endpoints.searchProject(projectId, query),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
  if (error) return <div className="error">{(error as Error).message}</div>;
  if (!data) return <p className="doc-empty">Searching…</p>;

  const { counts } = data;
  const total = counts.overview + counts.timeline + counts.minutes + counts.transcripts;
  const mark = (text: string) => <Highlight text={text} query={data.query} />;
  return (
    <section className={`project-search${isFetching ? " busy" : ""}`} aria-live="polite">
      <p className="search-summary">
        {total === 0 ? `Nothing in this project matches “${data.query}”.`
          : [["overview", "Overview"], ["timeline", "Timeline"], ["minutes", "minutes"], ["transcripts", "transcripts"]]
            .filter(([key]) => counts[key as keyof typeof counts] > 0)
            .map(([key, label]) => `${counts[key as keyof typeof counts]} in ${label}`).join(" · ")}
      </p>

      {data.overview.length > 0 && (
        <Group title="Overview">
          <ul className="search-list">
            {data.overview.map((item, i) => (
              <li key={i}>
                <span className="search-kind">{item.section}</span>
                <span className={item.done ? "search-done" : undefined}>{mark(item.text)}</span>
                {item.owner && <span className="search-meta"> · {item.owner}{item.due ? `, ${item.due}` : ""}</span>}
                {item.cites.map((cite) => (
                  <Link key={cite.session_id} className="insight-meeting" to={`/meetings/${cite.session_id}`} title={cite.title}>
                    {shortDate(cite.date)}
                  </Link>
                ))}
              </li>
            ))}
          </ul>
        </Group>
      )}

      {(data.timeline.topics.length > 0 || data.timeline.meetings.length > 0) && (
        <Group title="Timeline">
          {data.timeline.topics.map((topic) => (
            <div key={topic.title} className="search-meeting">
              <div className="search-meeting-head">
                <strong>{mark(topic.title)}</strong>
                <span className={`thread-state ${topic.state}`}>{topic.state === "settled" ? "Settled" : "Open"}</span>
              </div>
              <ul className="search-list">
                {topic.steps.map((step, i) => (
                  <li key={i}>
                    <Link className="search-when" to={`/meetings/${step.session_id}`}>{shortDate(step.date)} · {step.title}</Link>
                    {mark(step.text)}
                    <Moment sessionId={step.session_id} seconds={step.seconds} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {data.timeline.meetings.map((meeting) => (
            <div key={meeting.session_id} className="search-meeting">
              <MeetingHead meeting={meeting} />
              <ul className="search-list">
                {meeting.points.map((point, i) => (
                  <li key={i}>{mark(point.text)}<Moment sessionId={meeting.session_id} seconds={point.seconds} /></li>
                ))}
              </ul>
            </div>
          ))}
        </Group>
      )}

      {data.minutes.length > 0 && (
        <Group title="Minutes of meetings">
          {data.minutes.map((meeting) => (
            <div key={meeting.session_id} className="search-meeting">
              <MeetingHead meeting={meeting} />
              <ul className="search-list">
                {meeting.items.map((item, i) => (
                  <li key={i}>
                    <span className="search-kind">
                      {{ topic: "Topic", discussion: item.topic ?? "Discussion", action: "Action item", agenda: "Agenda" }[item.kind]}
                    </span>
                    {item.kind === "topic" ? mark(item.topic ?? "") : mark(item.text ?? "")}
                    {item.owner && <span className="search-meta"> · {item.owner}{item.due ? `, ${item.due}` : ""}</span>}
                    <Moment sessionId={meeting.session_id} seconds={item.seconds} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Group>
      )}

      {data.transcripts.length > 0 && (
        <Group title="Transcripts">
          {data.transcripts.map((meeting) => (
            <div key={meeting.session_id} className="search-meeting">
              <MeetingHead meeting={meeting} />
              <ul className="search-lines">
                {meeting.lines.map((line, i) => (
                  <li key={i}>
                    <Link className="stamp" to={`/meetings/${meeting.session_id}?t=${line.seconds}`} title="Play from here">
                      {clock(line.seconds)}
                    </Link>
                    <span><strong>{line.speaker}</strong> {mark(line.text)}</span>
                  </li>
                ))}
              </ul>
              {meeting.more > 0 && (
                <Link className="search-more" to={`/meetings/${meeting.session_id}`}>
                  and {meeting.more} more in this meeting →
                </Link>
              )}
            </div>
          ))}
        </Group>
      )}
    </section>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card search-group">
      <h2 className="doc-label">{title}</h2>
      {children}
    </div>
  );
}

function MeetingHead({ meeting }: { meeting: { session_id: string; title: string; date: string | null } }) {
  return (
    <div className="search-meeting-head">
      <Link to={`/meetings/${meeting.session_id}`}><strong>{meeting.title}</strong></Link>
      <span className="search-meta">{shortDate(meeting.date)}</span>
    </div>
  );
}

function Moment({ sessionId, seconds }: { sessionId: string; seconds: number | null }) {
  if (seconds == null) return null;
  return <Link className="cite" to={`/meetings/${sessionId}?t=${seconds}`} title={`Play from ${clock(seconds)}`}>{clock(seconds)}</Link>;
}

/** The text with each place the words appear marked, ignoring case. */
function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.toLocaleLowerCase();
  const lower = text.toLocaleLowerCase();
  if (!needle || lower.length !== text.length) {
    // A few letters change length when lower-cased; then don't risk a wrong mark.
    return <>{text}</>;
  }
  const parts: React.ReactNode[] = [];
  let from = 0;
  for (let at = lower.indexOf(needle); at >= 0; at = lower.indexOf(needle, at + needle.length)) {
    parts.push(<Fragment key={`t${from}`}>{text.slice(from, at)}</Fragment>);
    parts.push(<mark key={`m${at}`}>{text.slice(at, at + needle.length)}</mark>);
    from = at + needle.length;
  }
  parts.push(<Fragment key="end">{text.slice(from)}</Fragment>);
  return <>{parts}</>;
}

/** "3 Oct", with the year when it isn't this one. */
function shortDate(value: string | null): string {
  if (!value) return "—";
  const day = new Date(value);
  return day.toLocaleDateString(undefined, {
    day: "numeric", month: "short", ...(day.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
}

